/**
 * Article Hooks
 *
 * React Query hooks for article operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { parseApiError } from '@/lib/utils'
import type {
  Article,
  ArticleCreate,
  ArticleUpdate,
  ArticleFilters,
  PaginatedResponse,
  Family,
  Group,
  Provider,
  Ubication,
} from '@/types'

const ARTICLES_KEY = 'articles'
const NEXT_CODE_KEY = 'articleNextCode'
const FAMILIES_KEY = 'families'
const GROUPS_KEY = 'groups'
const PROVIDERS_KEY = 'providers'
const UBICATIONS_KEY = 'ubications'

// Article queries
export function useArticles(filters: ArticleFilters = {}) {
  return useQuery({
    queryKey: [ARTICLES_KEY, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.search) params.append('search', filters.search)
      if (filters.family_id) params.append('family_id', String(filters.family_id))
      if (filters.group_id) params.append('group_id', String(filters.group_id))
      if (filters.provider_id) params.append('provider_id', String(filters.provider_id))
      if (filters.ubication_id) params.append('ubication_id', String(filters.ubication_id))
      if (filters.low_stock) params.append('low_stock', 'true')
      if (filters.skip) params.append('skip', String(filters.skip))
      if (filters.limit) params.append('limit', String(filters.limit))
      if (filters.sort_by) params.append('sort_by', filters.sort_by)
      if (filters.sort_order) params.append('sort_order', filters.sort_order)

      const response = await api.get<PaginatedResponse<Article>>(
        `/articles?${params.toString()}`
      )
      return response.data
    },
  })
}

export function useArticle(id: number) {
  return useQuery({
    queryKey: [ARTICLES_KEY, id],
    queryFn: async () => {
      const response = await api.get<Article>(`/articles/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

export function useArticleByCode(code: string) {
  return useQuery({
    queryKey: [ARTICLES_KEY, 'code', code],
    queryFn: async () => {
      const response = await api.get<Article>(`/articles/code/${code}`)
      return response.data
    },
    enabled: !!code,
  })
}

export function useNextArticleCode(enabled: boolean = true) {
  return useQuery({
    queryKey: [NEXT_CODE_KEY],
    queryFn: async () => {
      const response = await api.get<{ next_code: string }>('/articles/next-code')
      return response.data.next_code
    },
    enabled,
  })
}

// Article mutations
export function useCreateArticle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ArticleCreate) => {
      const response = await api.post<Article>('/articles', data)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY], refetchType: 'all' })
      await queryClient.invalidateQueries({ queryKey: [NEXT_CODE_KEY], refetchType: 'all' })
    },
  })
}

export function useUpdateArticle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ArticleUpdate }) => {
      const response = await api.put<Article>(`/articles/${id}`, data)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY], refetchType: 'all' })
    },
  })
}

export function useDeleteArticle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, permanent = false }: { id: number; permanent?: boolean }) => {
      await api.delete(`/articles/${id}${permanent ? '?permanent=true' : ''}`)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY], refetchType: 'all' })
    },
  })
}

export function useRestoreArticle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<Article>(`/articles/${id}/restore`)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY], refetchType: 'all' })
    },
  })
}

export function useUpdateStock() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      quantity,
      reason,
    }: {
      id: number
      quantity: number
      reason?: string
    }) => {
      const response = await api.post<Article>(`/articles/${id}/stock`, {
        quantity,
        reason,
      })
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY], refetchType: 'all' })
    },
  })
}

export function useRecalculatePrices() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<Article>(
        `/articles/${id}/recalculate-prices`
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARTICLES_KEY], refetchType: 'all' })
    },
  })
}

// Catalog queries
export function useFamilies() {
  return useQuery({
    queryKey: [FAMILIES_KEY],
    queryFn: async () => {
      const response = await api.get<Family[]>('/articles/catalogs/families')
      return response.data
    },
  })
}

export function useGroups() {
  return useQuery({
    queryKey: [GROUPS_KEY],
    queryFn: async () => {
      const response = await api.get<Group[]>('/articles/catalogs/groups')
      return response.data
    },
  })
}

export function useProviders() {
  return useQuery({
    queryKey: [PROVIDERS_KEY],
    queryFn: async () => {
      const response = await api.get<Provider[]>('/articles/catalogs/providers')
      return response.data
    },
  })
}

export function useUbications() {
  return useQuery({
    queryKey: [UBICATIONS_KEY],
    queryFn: async () => {
      const response = await api.get<Ubication[]>('/articles/catalogs/ubications')
      return response.data
    },
  })
}

// Catalog mutations
export function useCreateFamily() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (name: string) => {
      const response = await api.post<Family>('/catalogs/families', { description: name })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FAMILIES_KEY] })
    },
  })
}

export function useCreateGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (name: string) => {
      const response = await api.post<Group>('/catalogs/groups', { description: name })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] })
    },
  })
}

export function useCreateProvider() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Partial<Provider>) => {
      const response = await api.post<Provider>('/catalogs/providers', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] })
    },
  })
}

export function useCreateUbication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (name: string) => {
      const response = await api.post<Ubication>('/catalogs/ubications', { name })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [UBICATIONS_KEY] })
    },
  })
}

export { parseApiError as getErrorMessage }
